import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Row,
  Col,
  CardBody,
  Card,
  Alert,
  Container,
  Form,
  Input,
  FormFeedback,
  Label,
  Button,
  Spinner,
} from 'reactstrap';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { useResetPasswordMutation } from 'http-client/hooks/authHooks';
import { ResetPasswordDto } from 'http-client/types/auth.types';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const emailOrPhone = location.state?.emailOrPhone || '';

  const { mutate: resetPassword, isPending, error } = useResetPasswordMutation();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      emailOrPhone: emailOrPhone,
      otp: '',
      newPassword: '',
      confirmPassword: '',
    } as ResetPasswordDto,
    validationSchema: Yup.object({
      otp: Yup.string()
        .matches(/^\d{6}$/, 'OTP must be a 6-digit number')
        .required('OTP is required'),
      newPassword: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .required('New Password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword')], 'Passwords must match')
        .required('Confirm Password is required'),
    }),
    onSubmit: (values) => {
      resetPassword(values, {
        onSuccess: () => {
          toast.success('Password reset successfully');
          navigate('/login');
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message ?? 'Password reset failed.');
        },
      });
    },
  });

  return (
    <React.Fragment>
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center py-5"
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              {/* Main Card */}
              <Card
                className="border-0 shadow-lg overflow-hidden"
                style={{ borderRadius: '1.5rem' }}
              >
                {/* Animated gradient header */}
                <div
                  className="position-relative"
                  style={{
                    background: 'linear-gradient(120deg, #4F46E5 0%, #7C3AED 100%)',
                    padding: '2rem 1.5rem',
                    textAlign: 'center',
                  }}
                >
                  <div className="mb-3">
                    <div className="bg-white bg-opacity-20 rounded-circle d-inline-flex p-3">
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 2L2 7L12 12L22 7L12 2Z"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 17L12 22L22 17"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2 12L12 17L22 12"
                          stroke="white"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                  <h2 className="text-white fw-bold mb-2" style={{ letterSpacing: '-0.02em' }}>
                    Reset Password
                  </h2>
                  <p className="text-white text-opacity-90 mb-0">
                    Enter OTP and create new password
                  </p>
                </div>

                <CardBody className="p-4 p-lg-5">
                  <div>
                    <div className="text-center mb-4">
                      <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex p-2 mb-3">
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M12 15V17M6 21H18C19.1046 21 20 20.1046 20 19V13C20 11.8954 19.1046 11 18 11H6C4.89543 11 4 11.8954 4 13V19C4 20.1046 4.89543 21 6 21ZM16 11V7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7V11H16Z"
                            stroke="#10B981"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <h4 className="fw-semibold mb-2">Create New Password</h4>
                      <p className="text-muted small">
                        Enter the OTP sent to your phone and set your new password.
                      </p>
                    </div>

                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      {error && (
                        <Alert color="danger" className="border-0 rounded-3 mb-4">
                          <div className="d-flex align-items-center">
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="me-2"
                            >
                              <path
                                d="M12 8V12M12 16H12.01M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            {error?.response?.data?.message || 'Reset failed'}
                          </div>
                        </Alert>
                      )}

                      <div className="mb-4">
                        <Label className="form-label fw-semibold text-secondary">
                          Email or Phone Number
                        </Label>
                        <Input
                          name="emailOrPhone"
                          className="form-control-lg border-0 bg-light rounded-3"
                          type="text"
                          value={validation.values.emailOrPhone}
                          readOnly
                          disabled
                          style={{ backgroundColor: '#F3F4F6' }}
                        />
                      </div>

                      <div className="mb-4">
                        <Label className="form-label fw-semibold text-secondary">OTP</Label>
                        <Input
                          name="otp"
                          className="form-control-lg border-0 bg-light rounded-3"
                          placeholder="Enter 6-digit OTP"
                          type="text"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.otp}
                          invalid={validation.touched.otp && !!validation.errors.otp}
                          style={{ backgroundColor: '#F3F4F6' }}
                        />
                        {validation.touched.otp && validation.errors.otp ? (
                          <FormFeedback type="invalid" className="mt-1">
                            {validation.errors.otp}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-4">
                        <Label className="form-label fw-semibold text-secondary">
                          New Password
                        </Label>
                        <Input
                          name="newPassword"
                          className="form-control-lg border-0 bg-light rounded-3"
                          placeholder="Enter new password"
                          type="password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.newPassword}
                          invalid={
                            validation.touched.newPassword && !!validation.errors.newPassword
                          }
                          style={{ backgroundColor: '#F3F4F6' }}
                        />
                        {validation.touched.newPassword && validation.errors.newPassword ? (
                          <FormFeedback type="invalid" className="mt-1">
                            {validation.errors.newPassword}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-4">
                        <Label className="form-label fw-semibold text-secondary">
                          Confirm Password
                        </Label>
                        <Input
                          name="confirmPassword"
                          className="form-control-lg border-0 bg-light rounded-3"
                          placeholder="Confirm password"
                          type="password"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.confirmPassword}
                          invalid={
                            validation.touched.confirmPassword &&
                            !!validation.errors.confirmPassword
                          }
                          style={{ backgroundColor: '#F3F4F6' }}
                        />
                        {validation.touched.confirmPassword && validation.errors.confirmPassword ? (
                          <FormFeedback type="invalid" className="mt-1">
                            {validation.errors.confirmPassword}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <Button
                        color="primary"
                        type="submit"
                        disabled={isPending}
                        className="w-100 btn-lg rounded-3 fw-semibold mb-3"
                        style={{
                          background: 'linear-gradient(120deg, #4F46E5 0%, #7C3AED 100%)',
                          border: 'none',
                          boxShadow: '0 4px 14px 0 rgba(79, 70, 229, 0.4)',
                        }}
                      >
                        {isPending ? (
                          <>
                            <Spinner size="sm" className="me-2" />
                            Resetting...
                          </>
                        ) : (
                          'Reset Password'
                        )}
                      </Button>

                      <div className="text-center">
                        <a
                          href="/login"
                          className="text-decoration-none small d-inline-flex align-items-center"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="me-1"
                          >
                            <path
                              d="M10 19L3 12M3 12L10 5M3 12H21"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          Back to Login
                        </a>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              {/* Footer */}
              <div className="text-center mt-4">
                <p className="text-white-50 small mb-0">
                  © {new Date().getFullYear()} Pharmacy Management. All rights reserved.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ResetPassword;
